/**
 * Authentication event logging utilities
 *
 * Provides structured logging for authentication events with correlation IDs
 * for troubleshooting and security monitoring.
 */

/**
 * Generate a correlation ID for tracking authentication flows
 *
 * Uses crypto.randomUUID() for browser environments that support it,
 * falls back to timestamp-based UUID for older browsers.
 */
export function generateCorrelationId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers: timestamp + random
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

/**
 * Authentication event types
 */
export type AuthEventType =
  | 'auth_attempt'
  | 'auth_success'
  | 'auth_failure'
  | 'token_refresh'
  | 'auth_error'
  | 'sign_out'
  | 'login_redirect'
  | 'logout_initiated'
  | 'logout_redirect'
  | 'logout_complete';

/**
 * Structured authentication event
 */
export interface AuthEvent {
  timestamp: string;
  correlationId: string;
  eventType: AuthEventType;
  message: string;
  userId?: string;
  userEmail?: string;
  errorType?: string;
  errorCode?: string;
  provider?: string;
  environment: string;
  userAgent: string;
}

/**
 * Log a structured authentication event
 *
 * Logs to console with structured format. In production, this could be
 * enhanced to send to a logging service.
 *
 * IMPORTANT: Never logs sensitive data (passwords, tokens, PII beyond user ID/email)
 */
export function logAuthEvent(
  eventType: AuthEventType,
  message: string,
  options?: {
    correlationId?: string;
    userId?: string;
    userEmail?: string;
    errorType?: string;
    errorCode?: string;
    error?: Error;
    provider?: string;
  }
): void {
  const correlationId = options?.correlationId || generateCorrelationId();

  const event: AuthEvent = {
    timestamp: new Date().toISOString(),
    correlationId,
    eventType,
    message,
    userId: options?.userId,
    userEmail: options?.userEmail,
    errorType: options?.errorType || options?.error?.name,
    errorCode: options?.errorCode,
    provider: options?.provider,
    environment: import.meta.env.MODE || 'unknown',
    userAgent: navigator.userAgent
  };

  // Log to console with appropriate level
  const logLevel = eventType.includes('error') || eventType.includes('failure')
    ? 'error'
    : eventType === 'auth_success'
    ? 'info'
    : 'log';

  console[logLevel](`[AUTH ${eventType.toUpperCase()}]`, event);
}

/**
 * Store correlation ID in session storage for auth flow tracking
 */
export function storeCorrelationId(correlationId: string): void {
  try {
    sessionStorage.setItem('auth_correlation_id', correlationId);
  } catch (error) {
    console.warn('Failed to store correlation ID:', error);
  }
}

/**
 * Retrieve correlation ID from session storage
 */
export function getStoredCorrelationId(): string | null {
  try {
    return sessionStorage.getItem('auth_correlation_id');
  } catch (error) {
    console.warn('Failed to retrieve correlation ID:', error);
    return null;
  }
}

/**
 * Clear correlation ID from session storage
 */
export function clearCorrelationId(): void {
  try {
    sessionStorage.removeItem('auth_correlation_id');
  } catch (error) {
    console.warn('Failed to clear correlation ID:', error);
  }
}
